import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const profileSchema = z.object({
  fullName: z.string().min(1, 'Required'),
  address: z.string().min(1, 'Required'),
  phone: z.string().min(7, 'Enter a valid phone number'),
});

export type ProfileValues = z.infer<typeof profileSchema>;

export function StepProfile({
  defaultValues,
  onNext,
  onBack,
}: {
  defaultValues: Partial<ProfileValues>;
  onNext: (data: ProfileValues) => void;
  onBack: () => void;
}) {
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: defaultValues.fullName ?? '',
      address: defaultValues.address ?? '',
      phone: defaultValues.phone ?? '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="fullName">{t.fullName}</Label>
        <Input
          id="fullName"
          placeholder={t.fullNamePlaceholder}
          aria-invalid={Boolean(errors.fullName)}
          {...register('fullName')}
        />
        {errors.fullName && (
          <p className="text-destructive text-xs">{t.required}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="address">{t.address}</Label>
        <Input
          id="address"
          placeholder={t.addressPlaceholder}
          aria-invalid={Boolean(errors.address)}
          {...register('address')}
        />
        {errors.address && (
          <p className="text-destructive text-xs">{t.required}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="phone">{t.phone}</Label>
        <Input
          id="phone"
          type="tel"
          placeholder={t.phonePlaceholder}
          aria-invalid={Boolean(errors.phone)}
          {...register('phone')}
        />
        {errors.phone && (
          <p className="text-destructive text-xs">{errors.phone.message}</p>
        )}
      </div>

      <div className="flex gap-3 mt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onBack}>
          {t.back}
        </Button>
        <Button type="submit" className="flex-1">
          {t.next}
        </Button>
      </div>
    </form>
  );
}
